import { FC, useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Range, getTrackBackground } from 'react-range';

import {
  containerStyle,
  labelStyle,
  valueStyle,
  sliderWrapperStyle,
  trackContainerStyle,
  thumbSpacingStyle,
  trackStyle,
  thumbStyle,
  thumbTransform
} from './styles.ts';

interface AgeRangeFilterProps {
  initialMinAge: number;
  initialMaxAge: number;
  onAgeChange: (min: number, max: number) => void;
}

export const MIN_AGE = 18;
export const MAX_AGE = 60;
const MIN_GAP = 2;
const THUMB_RADIUS = 17;

export const AgeRangeFilter: FC<AgeRangeFilterProps> = ({
  initialMinAge,
  initialMaxAge,
  onAgeChange
}) => {
  const { t } = useTranslation();
  const [values, setValues] = useState([initialMinAge, initialMaxAge]);

  const handleChange = useCallback(
    (newValues: number[]) => {
      const [newMin, newMax] = newValues;
      if (newMin === values[0] && newMax === values[1]) return;
      if (newMax - newMin >= MIN_GAP) setValues(newValues);
    },
    [values]
  );

  const handleFinalChange = useCallback(
    (newValues: number[]) => {
      const [newMin, newMax] = newValues;
      if (newMax - newMin >= MIN_GAP) onAgeChange(newMin, newMax);
    },
    [onAgeChange]
  );

  const trackBackground = useMemo(() => {
    return getTrackBackground({
      values,
      colors: ['#ccc', '#000', '#ccc'],
      min: MIN_AGE,
      max: MAX_AGE
    });
  }, [values]);

  return (
    <div>
      <div style={containerStyle}>
        <div style={labelStyle}>{t('search_filters.age_range')}</div>
        <div style={valueStyle}>{`${values[0]} - ${values[1]}`}</div>
      </div>

      <div style={sliderWrapperStyle}>
        <div style={trackContainerStyle}>
          <div style={thumbSpacingStyle(THUMB_RADIUS)} />

          <div style={{ flexGrow: 1, position: 'relative' }}>
            <Range
              step={1}
              min={MIN_AGE}
              max={MAX_AGE}
              values={values}
              onChange={handleChange}
              onFinalChange={handleFinalChange}
              renderTrack={({ props, children }) => (
                <div
                  {...props}
                  style={{ ...props.style, ...trackStyle(trackBackground) }}
                >
                  {children}
                </div>
              )}
              renderThumb={({ props, index }) => (
                <div
                  {...props}
                  style={{
                    ...props.style,
                    ...thumbStyle(THUMB_RADIUS),
                    ...thumbTransform(index)
                  }}
                />
              )}
            />
          </div>

          <div style={thumbSpacingStyle(THUMB_RADIUS)} />
        </div>
      </div>
    </div>
  );
};
